import type {ColorFormat} from "../types";
import type {Color} from "react-aria-components";

import {converter, formatCss, formatHsl, mapper, parse, parseOklch, round} from "culori";
import {parseColor} from "react-aria-components";

import {DEFAULT_COLOR_HSL} from "../constants";

/* -------------------------------------------------------------------------------------------------
 * Normalize Color Syntax
 * -----------------------------------------------------------------------------------------------*/

export function normalizeColorSyntax(color: string): string {
  const trimmed = color.trim();

  const hslMatch = trimmed.match(
    /^(hsla?)\s*\(\s*([^\s,]+)\s+([^\s,]+)\s+([^\s,/]+)(?:\s*\/\s*([^\s)]+))?\s*\)$/i,
  );

  if (hslMatch) {
    const [, func, h, s, l, a] = hslMatch;

    if (a !== undefined) return `hsla(${h}, ${s}, ${l}, ${a})`;

    return `${func}(${h}, ${s}, ${l})`;
  }

  const rgbMatch = trimmed.match(
    /^(rgba?)\s*\(\s*([^\s,]+)\s+([^\s,]+)\s+([^\s,/]+)(?:\s*\/\s*([^\s)]+))?\s*\)$/i,
  );

  if (rgbMatch) {
    const [, func, r, g, b, a] = rgbMatch;

    if (a !== undefined) return `rgba(${r}, ${g}, ${b}, ${a})`;

    return `${func}(${r}, ${g}, ${b})`;
  }

  const hsbMatch = trimmed.match(
    /^(hsba?)\s*\(\s*([^\s,]+)\s+([^\s,]+)\s+([^\s,/]+)(?:\s*\/\s*([^\s)]+))?\s*\)$/i,
  );

  if (hsbMatch) {
    const [, func, h, s, b, a] = hsbMatch;

    if (a !== undefined) return `hsba(${h}, ${s}, ${b}, ${a})`;

    return `${func}(${h}, ${s}, ${b})`;
  }

  return trimmed;
}

/* -------------------------------------------------------------------------------------------------
 * Detect Color Format
 * -----------------------------------------------------------------------------------------------*/

export function detectColorFormat(value: string): ColorFormat | null {
  const trimmed = value.trim().toLowerCase();

  if (trimmed.startsWith("oklch(")) return "oklch";
  if (trimmed.startsWith("hsl(") || trimmed.startsWith("hsla(")) return "hsl";
  if (trimmed.startsWith("rgb(") || trimmed.startsWith("rgba(")) return "rgb";
  if (trimmed.startsWith("hsb(") || trimmed.startsWith("hsba(")) return "hsb";
  if (/^#?[0-9a-f]{3,8}$/i.test(trimmed)) return "hex";

  return null;
}

/* -------------------------------------------------------------------------------------------------
 * Format Color
 * -----------------------------------------------------------------------------------------------*/

export function formatColor(color: Color, format: ColorFormat): string {
  if (format !== "oklch") {
    return color.toString(format);
  }
  const oklch = converter("oklch");
  const roundTo4 = round(4);
  const oklchColor = oklch(color.toString("css"));

  if (!oklchColor) return color.toString("hex");
  const roundedColor = mapper(roundTo4, "oklch")(oklchColor);

  return formatCss(roundedColor);
}

export function getHueFromColor(color: Color): number {
  const oklch = converter("oklch")(color.toString("hsl"));

  if (!oklch || oklch.h === undefined) return 0;

  return oklch.h;
}

export function formatFromOklchToColor(oklch: string): Color {
  const oklchColor = parseOklch(oklch);

  if (!oklchColor) return parseColor(DEFAULT_COLOR_HSL);
  const hsl = formatHsl(oklchColor);

  return parseColor(hsl ?? DEFAULT_COLOR_HSL);
}

export const getValuesFromOklch = (
  hsl: string,
): {lightness: number; chroma: number; hue: number} => {
  const oklch = converter("oklch");
  const oklchColor = oklch(hsl);

  return {chroma: oklchColor?.c ?? 0, hue: oklchColor?.h ?? 0, lightness: oklchColor?.l ?? 0};
};

/* -------------------------------------------------------------------------------------------------
 * Validate Color Input
 * -----------------------------------------------------------------------------------------------*/

export function validateColorInput(input: string, format: ColorFormat): Color | null {
  const trimmed = input.trim();

  if (!trimmed) return null;
  try {
    const parsed = parse(trimmed);

    if (!parsed) return null;
    switch (format) {
      case "hex":
        if (/^#?([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6}|[0-9A-Fa-f]{8})$/.test(trimmed)) {
          return parseColor(trimmed.startsWith("#") ? trimmed : `#${trimmed}`);
        }

        return null;
      case "hsl":
        if (/^hsla?\s*\(/i.test(trimmed)) return parseColor(normalizeColorSyntax(trimmed));

        return null;
      case "rgb":
        if (/^rgba?\s*\(/i.test(trimmed)) return parseColor(normalizeColorSyntax(trimmed));

        return null;
      case "hsb":
        if (/^hsba?\s*\(/i.test(trimmed)) return parseColor(normalizeColorSyntax(trimmed));

        return null;
      case "oklch":
        if (/^oklch\s*\(/i.test(trimmed)) {
          const hsl = formatHsl(trimmed);

          if (hsl) return parseColor(hsl);
        }

        return null;
      default:
        return null;
    }
  } catch {
    return null;
  }
}
